import React, { useCallback, useRef, useState } from 'react';
import { CafDashboard, CafCorpusAction, CafDashboardWindow, CafDecisionPrecedent, CafDecisionRow } from '../../../api/cafCorpus';
import { HoldGroupKey } from '../copy';
import { formatClock, formatCount } from '../format';
import { Icon } from '../Icon';
import { lenderFor, useLenderRules } from '../lenders';
import { HistoryDrawer } from '../teach/HistoryDrawer';
import { OneEmailModal } from '../teach/OneEmailModal';
import { DailyLine, GapList, OutcomeDonut, ReasonBars } from './charts';
import { DecisionDrawer } from './DecisionDrawer';
import { FixPendingStore } from './fixPendingStore';
import { lastIncludedUtcDay, toDailyPoints, toGapRows, toOutcomes, toReasonRows } from './howData';
import { RecentDecisions } from './RecentDecisions';
import { WatchingReport } from './WatchingReport';
import { useDashboardSession } from '../../../context/DashboardSessionContext';
import { useSnapshot } from './useSnapshot';

const WINDOWS: CafDashboardWindow[] = [7, 14, 30];
const BootstrapStatus: React.FC<{ snapshot: CafDashboard | null; loading: boolean; failed: boolean }> = ({ snapshot, loading, failed }) => {
  const progress = snapshot?.bootstrap;
  const code = snapshot?.last_refresh_error_code;
  const error = code === 'dashboard_migration_required' || code === 'workflow_metrics_schema_unavailable'
    ? 'Dashboard setup needs an update. Please contact an administrator.'
    : code === 'dashboard_sql_unavailable' ? 'Dashboard status is temporarily unavailable. Try Refresh.'
    : code && code !== 'dashboard_bootstrap_incomplete' ? 'The last preparation attempt failed. The dashboard will retry on its schedule.' : null;
  const phase = progress?.phase === 'coverage' ? 'checking example coverage'
    : progress?.phase === 'scanning_tasks' ? 'initial task scans in progress'
    : progress?.phase === 'scanning_history' ? 'initial history scans in progress'
    : progress?.phase === 'finalizing' ? 'final checks and figures pending' : 'waiting for the first figures';
  return <section className="ui-card cur-span-12" role="status">
    <div className="ui-card__body">
      <p>{!snapshot && loading ? 'Loading dashboard status…' : `Preparing dashboard: ${phase}.`}</p>
      {progress && <p>{progress.completed_initial_scans} of {progress.required_initial_scans} source scans finished.
        {' '}{progress.coverage_complete ? 'Example coverage checked.' : 'Example coverage is still being checked.'}
        {progress.active_attempt && ' A preparation attempt is running.'}</p>}
      {error && <p>{error}</p>}
      {failed && <p>Couldn&apos;t load dashboard status. Try Refresh.</p>}
      {snapshot?.refresh_attempt?.next_due_at && <p>Next scheduled attempt around {formatClock(snapshot.refresh_attempt.next_due_at)}.</p>}
    </div>
  </section>;
};
const NotYet = () => <div className="cur-empty">Not available yet</div>;

const Card: React.FC<{ span: 5 | 7 | 12; title: string; meta?: React.ReactNode; children: React.ReactNode; id?: string }> = ({ span, title, meta, children, id }) => (
  <section className={`ui-card cur-span-${span}`} aria-label={title} id={id}>
    <div className="ui-card__header"><h2 className="ui-card__title">{title}</h2>{meta && <span className="cur-muted cur-small cur-tnum">{meta}</span>}</div>
    <div className="ui-card__body">{children}</div>
  </section>
);

/**
 * Task 23: the How it's doing tab. Charts first (what happened, handled by
 * AI day by day, why emails were marked as manual, senders with no examples
 * yet), then Historical data as of, then Recent decisions, then the decision drawer /
 * teach modal / history drawer wiring. `onLabelSender` and `onShowExample`
 * are wired up by the page shell in Task 24.
 */
export const HowTab: React.FC<{ onLabelSender: (sender: string) => void; onReviewSender?: (sender: string) => void; onShowExample: (entryId: string, sender: string) => void; canAutomate?: boolean }> = ({ onLabelSender, onReviewSender, onShowExample, canAutomate = false }) => {
  const session = useDashboardSession();
  const [selection, setSelection] = useState({ owner: session, days: session.days });
  const days = selection.owner === session ? selection.days : session.days;
  const setDays = (value: CafDashboardWindow) => {
    session.days = value;
    setSelection({ owner: session, days: value });
  };
  const { snapshot, response, loading, failed, refresh } = useSnapshot(days, session);
  const rules = useLenderRules();
  const lenderOf = useCallback((sender: string) => lenderFor(sender, rules), [rules]);
  const [reason, setReason] = useState<HoldGroupKey | null>(null);
  const [open, setOpen] = useState<CafDecisionRow | null>(null);
  const [teach, setTeach] = useState<{ row: CafDecisionRow; correction?: { decisionId: string; reviewRevision: number; feedbackOnly?: boolean } } | null>(null);
  const [history, setHistory] = useState<CafDecisionPrecedent | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  const [showHistorical, setShowHistorical] = useState(false);
  // Controller note 9: survives DecisionDrawer's own remount (key={row.id})
  // across a close/reopen of the same row, for 2 minutes.
  const fixStoreRef = useRef(new FixPendingStore());

  const ready = snapshot && snapshot.status === 'ready' ? snapshot : null;
  const throughput = ready?.throughput ?? null;
  const outcomes = throughput ? toOutcomes(throughput) : null;
  // Controller note 2: attached-but-left-open has no hold_reason code of its
  // own -- it's read from the same effect toOutcomes folds into "staff".
  const assignedCount = throughput?.effects?.assigned?.distinct_tasks ?? 0;
  const reasonRows = throughput?.hold_reasons ? toReasonRows(throughput.hold_reasons) : null;
  const reasonTotal = reasonRows ? reasonRows.reduce((sum, row) => sum + row.count, 0) + assignedCount : 0;
  // Controller note 3: fill the daily line up to the day before the window
  // ends, when the snapshot says so -- otherwise leave the sparse data as is.
  const windowEnd = ready?.measurement?.window_end_exclusive
    ? lastIncludedUtcDay(ready.measurement.window_end_exclusive) ?? undefined
    : undefined;
  const dailyPoints = throughput?.daily ? toDailyPoints(throughput.daily, windowEnd) : null;
  const accuracy = ready?.measurement?.accuracy ?? null;
  const asOf = ready?.measurement?.as_of ?? null;
  // Controller note 7: no guessed numbers -- accuracy missing or nothing
  // checked yet both mean "say nothing", never "0 right 0 wrong".
  const checked = accuracy ? accuracy.correct + accuracy.incorrect : 0;

  // Final wave item 2: a refreshed feed hands the open drawer its reloaded row (same id, so
  // the drawer keeps its state), and a close/reopen always starts from the latest row.
  const swapOpen = useCallback((rows: CafDecisionRow[]) => {
    setOpen((current) => (current ? rows.find((candidate) => candidate.id === current.id) ?? current : current));
  }, []);

  const pickReason = (key: HoldGroupKey | null) => {
    setReason(key);
    const recent = document.getElementById('cur-recent');
    if (key && recent && typeof recent.scrollIntoView === 'function') recent.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <div className="cur-dash-bar">
        <div className="cur-seg" role="group" aria-label="Time period">
          {WINDOWS.map((value) => (
            <button key={value} type="button" aria-pressed={days === value} onClick={() => { setDays(value); setReason(null); }}>{value} days</button>
          ))}
        </div>
        <span className="cur-spacer" />
        {failed && ready && <span className="cur-hint">Couldn&apos;t refresh. Showing the last numbers.</span>}
        {(asOf || loading) && <span className="cur-asof">{asOf ? `Historical data as of ${formatClock(asOf)}` : 'Loading…'}</span>}
        <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => { refresh(); setRefreshKey(key => key + 1); }} disabled={loading}>
          <Icon name="refresh" size="sm" />{loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {ready && (ready.stale || (response?.last_refresh_error_code && response.last_refresh_error_code !== 'dashboard_bootstrap_incomplete')) &&
        <p className="cur-hint">Showing the last published figures. The scheduled update is still pending.</p>}
      <div className="cur-dash-grid">
        <WatchingReport key={`${days}-${refreshKey}`} days={days} refreshKey={refreshKey} session={session} lenderOf={lenderOf}
          onLabelSender={onLabelSender} onShowExample={onShowExample} canAutomate={canAutomate} />
        <Card span={12} title="Senders to review">
          {ready?.coverage && ready.coverage.status === 'ready'
            ? <GapList rows={toGapRows(ready.coverage, lenderOf)} onLabelNow={onReviewSender ?? onLabelSender} />
            : <NotYet />}
        </Card>
        <section className="cur-span-12" aria-label="Historical actions">
          <h2><button type="button" className="cur-linkish" aria-expanded={showHistorical} onClick={() => setShowHistorical((value) => !value)}>Historical actions</button></h2>
          {showHistorical && <>
          <p className="cur-hint">Committed actions and staff checks from the published historical figures. These use their historical counting rules.</p>
          <div className="cur-dash-grid">
        {ready ? <>
        <Card span={5} title="What happened to emails"
          meta={outcomes ? `${formatCount(outcomes.done + outcomes.archived + outcomes.staff)} emails · last ${days} days` : undefined}>
          {outcomes ? <OutcomeDonut counts={outcomes} /> : <NotYet />}
        </Card>
        <Card span={7} title="Handled by AI, day by day" meta="share of that day's emails">
          {!throughput ? <NotYet /> : dailyPoints ? <DailyLine points={dailyPoints} /> : <NotYet />}
        </Card>
        <Card span={7} title="Why emails were marked as manual"
          meta={reasonRows && reasonTotal > 0 ? `${formatCount(reasonTotal)} emails · click a bar to list them` : undefined}>
          {!throughput ? <NotYet /> : reasonRows ? (
            <ReasonBars rows={reasonRows} selected={reason} onSelect={pickReason}
              extra={assignedCount > 0 ? { label: 'Attached, left open for a person', count: assignedCount } : null} />
          ) : <NotYet />}
        </Card>
        </> : <BootstrapStatus snapshot={response ?? snapshot} loading={loading} failed={failed} />}
        <section className="ui-card cur-span-12" id="cur-recent" aria-label="Recent decisions">
          <div className="ui-card__header">
            <h2 className="ui-card__title">Recent decisions</h2>
            {accuracy && checked > 0 && (
              <div className="cur-staff-check">
                <span>Staff checked {formatCount(checked)} in this period</span>
                <svg viewBox="0 0 120 8" aria-hidden="true">
                  <rect x="0" y="0" width={(accuracy.correct / checked) * 120} height="8" className="cur-sc-right" />
                  <rect x={(accuracy.correct / checked) * 120} y="0" width={(accuracy.incorrect / checked) * 120} height="8" className="cur-sc-wrong" />
                </svg>
                <span className="cur-v-right" style={{ fontWeight: 600 }}>{formatCount(accuracy.correct)} right</span>
                <span className="cur-v-wrong" style={{ fontWeight: 600 }}>{formatCount(accuracy.incorrect)} wrong</span>
              </div>
            )}
          </div>
          <div className="ui-card__body">
            <RecentDecisions days={days} lenderOf={lenderOf} reason={reason} onClearReason={() => setReason(null)}
              onOpen={(row) => setOpen(row)} refreshKey={refreshKey} onLoaded={swapOpen} />
          </div>
        </section>
          </div>
          </>}
        </section>
      </div>
      {open && (
        <DecisionDrawer key={open.id} row={open} lenderOf={lenderOf} reviewRefreshKey={reviewRefreshKey} onClose={() => setOpen(null)}
          onChanged={() => { setRefreshKey((key) => key + 1); refresh(); }}
          onLabelSender={(sender) => { setOpen(null); onLabelSender(sender); }}
          onTeach={(row, correction) => setTeach({ row, correction })} onShowExampleHistory={(precedent) => setHistory(precedent)}
          fixPending={fixStoreRef.current.isPending(open.id) || undefined}
          onFixPendingChange={(pending) => fixStoreRef.current.set(open.id, pending)} />
      )}
      {teach && (
        <OneEmailModal senders={[]} preset={{ taskId: teach.row.id, sender: teach.row.email_from, subject: teach.row.subject }} correction={teach.correction} canAutomate={canAutomate}
          onClose={() => setTeach(null)} onSaved={() => { setReviewRefreshKey((key) => key + 1); setRefreshKey((key) => key + 1); }}
          onShowExample={(id, sender) => { setTeach(null); setOpen(null); onShowExample(id, sender); }} />
      )}
      {history && history.sender && history.correct_action && (
        <HistoryDrawer
          // A shown precedent is never `missing` (see DecisionDrawer's gate),
          // so sender/correct_action come from a real corpus entry together --
          // narrowed to what HistoryDrawer actually reads, never `as unknown as`.
          entry={{ id: history.id, sender: history.sender, correct_action: history.correct_action as CafCorpusAction }}
          lenderOf={lenderOf} onClose={() => setHistory(null)} />
      )}
    </div>
  );
};
